import { IField } from '@eventr/common'
import {
    ColoursObj,
    FileWithStatus,
    Icon,
    Loading,
    TDialogStore,
    useDialogStore,
} from '@eventr/common-storybook'
import { Box, Card, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { FilePickerDialog } from '../../../common/FilePickerDialog'
import i18n from '../../../i18n/i18next'
import { useBoundStore } from '../../../state/bound.store'
import { defaultAddSnack } from '../../../state/overlayState/overlayContext'
import { useEditSectionContext } from '../editSectionProvider'

interface IImageSelectionEditFieldProps {
    field: IField
    _id?: string
}

export const ImageEditField = ({
    field,
    _id,
}: IImageSelectionEditFieldProps) => {
    const { getValue } = useEditSectionContext()
    const { updateValues } = useEditSectionContext()
    const { replaceDialog }: TDialogStore = useDialogStore(
        useShallow((s: TDialogStore) => ({
            replaceDialog: s.replaceDialog,
        }))
    )

    const [state, setState] = useState<string>()
    const [init, setInit] = useState<boolean>(false)
    const [uploading, setUploading] = useState<boolean>(false)

    useEffect(() => {
        if (init) return

        const initFieldValue = getValue(field, _id)
        setState(initFieldValue ?? '')

        setInit(true)
    }, [field, _id, getValue, init, setInit])

    const onFilesChanged = async (files: FileWithStatus[]) => {
        if (files.length > 0) {
            if (files[0].file.size > 6000000) {
                defaultAddSnack(
                    'File size is too large. Please upload a file less than 6mb.',
                    'error'
                )
                return
            }
            setUploading(true)
            try {
                const resp = await useBoundStore
                    .getState()
                    .uploadMedia(files[0].file, 'HeaderImage')

                if (resp.status === 'success') {
                    updateValues({
                        value: resp.data?.source_url,
                        field: field,
                        _id: _id,
                    })
                    setState(resp.data?.source_url)
                } else {
                    if (
                        resp.message ===
                        'Sorry, you are not allowed to upload this file type.'
                    ) {
                        defaultAddSnack(
                            i18n.t('edit-itinerary.fields.errorWrongImageType'),
                            'error'
                        )
                    }
                }
            } catch {
                defaultAddSnack(
                    i18n.t('edit-itinerary.fields.errorWithImageUpload'),
                    'error'
                )
            } finally {
                setUploading(false)
            }
        }
    }
    return (
        <Box>
            <Box padding={'0 0 4px 0'}>
                <Typography variant="bodySubtext">
                    {i18n.t('edit-itinerary.fields.cover-image')}
                </Typography>
            </Box>
            <Card
                variant="outlined"
                style={{
                    background: ColoursObj.backgrounds.input,
                    height: 'fit-content',
                }}
                onClick={() => {
                    replaceDialog({
                        node: (
                            <FilePickerDialog
                                onFileSelected={(file: File) => {
                                    onFilesChanged([
                                        {
                                            file: file,
                                            status: 'uploaded',
                                        },
                                    ])
                                }}
                            />
                        ),
                        backdropClosable: false,
                    })
                }}
            >
                <Stack
                    borderRadius={'inherit'}
                    style={{
                        height: 'calc(100vw * 6 / 16)',
                        backgroundImage: 'url(' + state + ')',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                    padding="16px"
                >
                    <Stack
                        alignItems={'center'}
                        justifyContent={'center'}
                        gap={'8px'}
                        height={'100%'}
                    >
                        {!state && (
                            <>
                                <Icon name="Upload" />
                                <Typography variant="bodyBold" align="center">
                                    {i18n.t(
                                        'edit-itinerary.fields.click-to-upload'
                                    )}
                                </Typography>
                                <Typography variant="bodySubtext">{`PNG or JPG  (max 6mb)`}</Typography>
                            </>
                        )}
                        {uploading && <Loading />}
                    </Stack>
                </Stack>
            </Card>
        </Box>
    )
}
